import { Download, Printer, Send, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, money, shortDate, statusClass } from "../lib/api";
import { FlowSphereLogo } from "../components/FlowSphereLogo";

export default function InvoicePreview({ invoice, profile = {}, currency, onClose, onRefresh, onRecordPayment }) {
  const subtotal = invoice.subtotal ?? (invoice.items || []).reduce((sum, item) => sum + item.quantity * item.unit_price, 0);
  const tax = invoice.tax ?? subtotal * (invoice.tax_rate || 0) / 100;
  const total = invoice.total ?? subtotal + tax;
  const outstanding = Math.max(total - (invoice.paid || 0), 0);
  const hasLetterhead = !!profile.letterhead_path;
  const letterheadUrl = "/api/business/letterhead";

  const confirmInvoice = async () => {
    try {
      await api.post(`/invoices/${invoice.id}/confirm`);
      toast.success("Invoice confirmed and stock adjusted.");
      onRefresh?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not confirm this invoice");
    }
  };

  const download = () => {
    const html = document.querySelector(".invoice-preview-sheet")?.outerHTML;
    if (!html) return;
    const blob = new Blob([
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${invoice.number}</title><style>@page{size:A4;margin:14mm}body{font-family:Manrope,Arial,sans-serif;color:#12231d;background:#fff;margin:0}.invoice-preview-sheet{width:182mm;margin:0 auto}.sheet-letterhead{margin-bottom:20px}.sheet-letterhead img{width:100%;max-height:150px;object-fit:contain;display:block}h1{letter-spacing:-1.2px}table{width:100%;border-collapse:collapse;margin-top:18px}th,td{padding:12px 14px;text-align:left;font-size:12px;border-bottom:1px solid #e3eae5}th{background:#f4f8f5;text-transform:uppercase;font-size:10px;letter-spacing:.8px;color:#708078}.totals{margin-top:22px;display:flex;justify-content:flex-end}.totals table{width:320px}.totals td{border:0;padding:6px 0}.totals tr:last-child td{font-weight:800;font-size:15px;padding-top:10px;border-top:1px solid #12231d}</style></head><body>${html}</body></html>`,
    ], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${invoice.number}.html`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="modal-backdrop invoice-preview-backdrop">
      <section className="invoice-preview" role="dialog" aria-modal="true" data-testid="invoice-preview-modal">
        <header className="invoice-preview-toolbar">
          <div>
            <p className="eyebrow">FLOWSPHERE / INVOICE</p>
            <h2>{invoice.number}</h2>
            <span className={`status ${statusClass(invoice.status)}`}>{invoice.status}</span>
          </div>
          <div className="invoice-preview-actions">
            {invoice.status === "Draft" && (
              <button data-testid="invoice-preview-confirm-button" className="primary-button" onClick={confirmInvoice}>
                <CheckCircle2 size={15} /> Confirm invoice
              </button>
            )}
            {invoice.status !== "Paid" && invoice.status !== "Draft" && (
              <button data-testid="invoice-preview-payment-button" className="primary-button" onClick={() => onRecordPayment?.(invoice)}>
                <Send size={15} /> Record payment
              </button>
            )}
            <button data-testid="invoice-preview-print-button" className="secondary-button" onClick={() => window.print()}>
              <Printer size={14} /> Print
            </button>
            <button data-testid="invoice-preview-download-button" className="secondary-button" onClick={download}>
              <Download size={14} /> Download
            </button>
            <button data-testid="invoice-preview-close-button" className="icon-button" onClick={onClose}><X size={18} /></button>
          </div>
        </header>
        <div className="invoice-preview-sheet invoice-a4" data-testid="invoice-preview-sheet">
          {hasLetterhead && (
            <div className="sheet-letterhead" data-testid="invoice-letterhead">
              <img src={letterheadUrl} alt={profile.business_name || "Business letterhead"} crossOrigin="use-credentials" />
            </div>
          )}
          <div className="sheet-header">
            <div className="business-block">
              {hasLetterhead ? (
                <>
                  <p className="eyebrow">BILL TO</p>
                  <strong style={{ color: "#0A1628", fontSize: 15, display: "block", margin: "2px 0 6px" }}>{invoice.customer?.name || invoice.customer_name}</strong>
                  {invoice.customer?.company && <p>{invoice.customer.company}</p>}
                  {invoice.customer?.address && <p>{invoice.customer.address}</p>}
                  {invoice.customer?.email && <p>{invoice.customer.email}</p>}
                  {invoice.customer?.tax_id && <p>GSTIN: {invoice.customer.tax_id}</p>}
                </>
              ) : (
                <>
                  <div className="brand-mark"><FlowSphereLogo variant="invoice" /></div>
                  <p style={{ marginTop: 12 }}><strong style={{ color: "#0A1628", fontSize: 13 }}>{profile.business_name || "FlowSphere Studio"}</strong></p>
                  <p>{profile.address || "12 Green Lane, Bengaluru 560001"}</p>
                  <p>{profile.email || "billing@flowsphere.app"} · {profile.phone || "+91 98450 12345"}</p>
                  <p>GSTIN: {profile.tax_id || "29ABCDE1234F1Z5"}</p>
                </>
              )}
            </div>
            <div className="sheet-meta">
              <h1>Tax Invoice</h1>
              <div className="meta-grid">
                <div><span>Invoice</span><strong>{invoice.number}</strong></div>
                <div><span>Issued</span><strong>{shortDate(invoice.created_at)}</strong></div>
                <div><span>Due</span><strong>{shortDate(invoice.due_date)}</strong></div>
                <div><span>Currency</span><strong>{currency}</strong></div>
              </div>
            </div>
          </div>
          <div className="sheet-parties">
            {!hasLetterhead && (
              <div>
                <p className="eyebrow">BILL TO</p>
                <strong>{invoice.customer?.name || invoice.customer_name}</strong>
                {invoice.customer?.company && <p>{invoice.customer.company}</p>}
                {invoice.customer?.address && <p>{invoice.customer.address}</p>}
                {invoice.customer?.email && <p>{invoice.customer.email}</p>}
                {invoice.customer?.tax_id && <p>GSTIN: {invoice.customer.tax_id}</p>}
              </div>
            )}
            <div>
              <p className="eyebrow">PAYMENT DETAILS</p>
              <p>{profile.default_terms || "Payment due within 14 days. Bank: HDFC · A/c 0122 3344 5566"}</p>
            </div>
          </div>
          <table className="sheet-table" data-testid="invoice-preview-line-items">
            <thead>
              <tr><th>Description</th><th>Qty</th><th>Rate</th><th className="right">Amount</th></tr>
            </thead>
            <tbody>
              {(invoice.items || []).map((item, index) => (
                <tr key={index}>
                  <td><strong>{item.description}</strong></td>
                  <td>{item.quantity}</td>
                  <td>{money(item.unit_price, currency)}</td>
                  <td className="right">{money(item.quantity * item.unit_price, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="sheet-totals">
            <div className="totals-block">
              <div><span>Subtotal</span><strong>{money(subtotal, currency)}</strong></div>
              <div><span>GST ({invoice.tax_rate || 0}%){invoice.tax_inclusive ? " · incl." : ""}</span><strong>{money(tax, currency)}</strong></div>
              <div className="grand"><span>Total due</span><strong>{money(total, currency)}</strong></div>
              {invoice.paid > 0 && (
                <>
                  <div><span>Paid</span><strong>−{money(invoice.paid, currency)}</strong></div>
                  <div className="grand"><span>Balance</span><strong>{money(outstanding, currency)}</strong></div>
                </>
              )}
            </div>
          </div>
          {invoice.notes && (
            <div className="sheet-notes">
              <p className="eyebrow">NOTES</p>
              <p>{invoice.notes}</p>
            </div>
          )}
          <div className="sheet-footer">
            <p className="eyebrow">TERMS</p>
            <p>{invoice.terms || profile.default_terms || "Payment due within 14 days."}</p>
            <small>{profile.footer_note || "Thank you for your business."}</small>
          </div>
        </div>
      </section>
    </div>
  );
}
